import { defineCopy } from '../pageCopy';
import { getProduct } from '../../products';

const product = getProduct('dilacrm')!;

const en = {
  seo: { title: product.seo.title, description: product.seo.description },
  hero: {
    title: 'every message is a deal',
    lede: "One inbox for WhatsApp, Instagram, Messenger, Telegram, TikTok and email, with AI that drafts in your customer's language.",
    secondary: 'See the inbox',
    liveLabel: product.liveLabel,
    imageAlt: product.imageAlt,
    chipDraft: 'AI draft · awaiting approval',
    chipEmail: 'Email',
  },
  marquee: [
    'WhatsApp',
    'Instagram DMs',
    'Messenger',
    'Telegram',
    'TikTok',
    'Email',
    'Voice AI',
    'کوردی',
    'العربية',
    'English',
  ],
  inbox: {
    aria: 'Unified inbox',
    title: 'one inbox. every channel.',
    lede: 'Connect the WhatsApp you already use, your Instagram, your page, your Telegram bot and your mailbox. Everything lands in one queue with lanes, snooze and presence.',
    rows: [
      {
        title: 'Context on every thread',
        body: 'Contact details, tags, deals and lead temperature sit beside the conversation, not in another tab.',
      },
      {
        title: 'The clock is visible',
        body: "Each channel's messaging window is tracked, with an approved-template escape when the window closes.",
      },
    ],
    shotAlt:
      'DilaCRM conversation view with a Messenger thread, CRM side panel, lead card and reply-window countdown.',
  },
  loop: {
    aria: 'AI approval',
    kicker: 'Human in the loop',
    title: 'your ai drafts. you approve.',
    lede: 'Three autonomy modes, armed per channel. High-confidence replies can go out on their own; everything else queues as a draft with the reason it paused. Nothing goes out unsupervised.',
    rows: [
      {
        title: 'Drafts only',
        body: 'AI writes, humans send. Every reply waits for an approve, an edit, or a reject with a reason it learns from.',
      },
      {
        title: 'Replies when confident',
        body: 'Above your confidence threshold it sends and schedules the follow-up. Below it, the draft lands in your queue.',
      },
      {
        title: 'Fully autonomous',
        body: 'Arm it per channel with abuse protection watching for flooding, manipulation and free-chatbot tourists.',
      },
    ],
    note: 'Grounded in your knowledge base · playbooks for real estate, solar, hospitality and grocery',
  },
  comments: {
    aria: 'Comment automation',
    shotAlt:
      'DilaCRM comments manager showing an Instagram comment in Sorani Kurdish with an AI-drafted reply pending approval.',
    title: 'comments become customers',
    lede: 'Facebook, Instagram and TikTok comments in one queue. AI drafts the public reply and the private DM, hides the toxic spam, and answers in Sorani when the comment is in Sorani.',
    rows: [
      {
        title: 'Comment to DM, on autopilot',
        body: '“Comment PRICE and we will message you.” Keyword rules turn ad comments into DM conversations, ManyChat-style, without another subscription.',
      },
      {
        title: 'Moderation that never sleeps',
        body: 'Abuse is classified and auto-hidden with per-day caps, per-channel opt-in and a full audit trail.',
      },
    ],
  },
  campaigns: {
    aria: 'Campaigns and ads',
    title: 'campaigns that land',
    body: 'Approved WhatsApp templates to everyone, one language, your VIP batch, or repeat-loyal customers. Every recipient tracked from sent to read, every failed message refunded to your wallet.',
    shotAlt:
      'DilaCRM WhatsApp campaign composer with credit balance, template picker, audience options and estimated cost.',
    adsTitle: 'ad spend, finally honest',
    adsBody:
      'Meta and TikTok accounts side by side: spend, reach, CTR, CPC, conversions and ROAS. Pause a campaign or fix a budget without leaving the screen.',
    adsAlt:
      'DilaCRM ads performance dashboard for TikTok showing spend, impressions, clicks, conversions and ROAS with a spend-over-time chart.',
    metricsAria: 'Tracked ad metrics',
    metrics: ['Spend', 'Reach', 'CTR', 'CPC', 'CPM', 'Conversions', 'ROAS'],
  },
  crm: {
    aria: 'CRM pipeline',
    title: 'a crm that fills itself',
    lede: 'Contacts are born from conversations, deals carry dual IQD and USD values, and the pipeline tells you which lead is going cold before it does.',
    shotAlt:
      'DilaCRM Kanban pipeline with new inquiry, qualified, site visit and quoted stages holding IQD and USD deal values.',
    rows: [
      {
        title: 'Rotting alerts per stage',
        body: 'Each stage gets its own “gone quiet after N days” threshold, so follow-ups are triggered before the lead forgets you.',
      },
      {
        title: 'Hot, warm, cold at a glance',
        body: 'Lead temperature, last AI signal and next-best-action sit on the card, and round-robin routing assigns new leads fairly.',
      },
      {
        title: 'Imports without the spreadsheet fight',
        body: 'CSV and Excel uploads up to 5,000 rows with AI column mapping, plus duplicate merge that keeps every conversation.',
      },
    ],
  },
  stats: {
    aria: 'DilaCRM in numbers',
    labels: ['Messaging channels', 'Interface languages', 'AI reply languages', 'AI on shift'],
  },
  languages: {
    aria: 'Languages',
    title: 'the only inbox that speaks sorani',
    lede: 'A full right-to-left interface, and an AI that answers in Kurdish Sorani, Arabic or English without being asked twice. Your customers write how they write; it meets them there.',
    rtlAlt:
      'DilaCRM inbox fully mirrored in Arabic, showing the right-to-left interface, wallet card and setup checklist.',
    rtlCaption: 'Full RTL, Arabic and Kurdish',
    channelsAlt:
      'DilaCRM channel connection page for Meta, WhatsApp and TikTok with per-service checkboxes and QR WhatsApp pairing.',
    channelsCaption: 'Connect a channel in five minutes',
  },
  demo: {
    title: 'stop losing the 11:47 pm lead',
    sub: 'A lead messaged at 11:47 PM. DilaCRM answered before they could leave. Put that on every channel you own.',
    secondary: 'Open the live inbox',
  },
};

type Copy = typeof en;

const ar: Copy = {
  seo: {
    title: 'DilaCRM: صندوق رسائل بالذكاء الاصطناعي لـ WhatsApp وInstagram وTelegram وغيرها',
    description:
      'DilaCRM من Datacode يجمع كل قنوات زبائنك بصندوق رسائل واحد بالذكاء الاصطناعي: WhatsApp وInstagram وMessenger وTelegram وTikTok والبريد الإلكتروني، مع ردود ذكاء اصطناعي بالكوردي والعربي والإنكليزي، وأتمتة التعليقات، وحملات WhatsApp، وتحليلات الإعلانات، وCRM مدمج.',
  },
  hero: {
    title: 'كل رسالة صفقة',
    lede: 'صندوق رسائل واحد لـ WhatsApp وInstagram وMessenger وTelegram وTikTok والبريد الإلكتروني، ومعاه ذكاء اصطناعي يكتب المسودة بلغة زبونك.',
    secondary: 'شوف صندوق الرسائل',
    liveLabel: 'صندوق الرسائل المباشر',
    imageAlt:
      'صندوق رسائل DilaCRM يعرض رداً مقترحاً من الذكاء الاصطناعي بانتظار موافقة الموظف، مع أزرار إرسال وتعديل وعدم الإرسال.',
    chipDraft: 'مسودة AI · بانتظار الموافقة',
    chipEmail: 'البريد',
  },
  marquee: [
    'WhatsApp',
    'رسائل Instagram',
    'Messenger',
    'Telegram',
    'TikTok',
    'البريد الإلكتروني',
    'ذكاء صوتي',
    'کوردی',
    'العربية',
    'English',
  ],
  inbox: {
    aria: 'صندوق الرسائل الموحد',
    title: 'صندوق واحد. كل القنوات.',
    lede: 'اربط الـ WhatsApp اللي تستخدمه هسة، وInstagram، وصفحتك، وبوت Telegram، وبريدك الإلكتروني. كل شي يوصل بطابور واحد فيه مسارات وتأجيل ومؤشر حضور.',
    rows: [
      {
        title: 'السياق بكل محادثة',
        body: 'بيانات الزبون والوسوم والصفقات ودرجة حرارة العميل المحتمل تكون جنب المحادثة، مو بتبويب ثاني.',
      },
      {
        title: 'الوقت واضح قدامك',
        body: 'نتابع نافذة المراسلة لكل قناة، ولما تنغلق النافذة تكدر تستخدم قالباً معتمداً.',
      },
    ],
    shotAlt:
      'شاشة محادثة بـ DilaCRM فيها رسائل Messenger، ولوحة CRM جانبية، وبطاقة العميل المحتمل، وعداد نافذة الرد.',
  },
  loop: {
    aria: 'موافقة الذكاء الاصطناعي',
    kicker: 'الإنسان بالحلقة',
    title: 'الـ AI يكتب. وانت توافق.',
    lede: 'ثلاثة أوضاع للاستقلالية، تفعّلها لكل قناة. الردود اللي الذكاء واثق منها بشكل عالي ممكن تنرسل لحالها، وباقي الردود تنتظر كمسودة مع سبب التوقف. ماكو شي يطلع بدون إشراف.',
    rows: [
      {
        title: 'مسودات فقط',
        body: 'الـ AI يكتب والإنسان يرسل. كل رد ينتظر موافقة، أو تعديل، أو رفض مع سبب يتعلم منه.',
      },
      {
        title: 'يرد إذا كان واثق',
        body: 'إذا تعدى حد الثقة اللي تحدده، يرسل الرد ويجدول المتابعة. وإذا نزل عنه، المسودة توصل لطابورك.',
      },
      {
        title: 'مستقل بالكامل',
        body: 'فعّله لكل قناة، ومعاه حماية من الإساءة تراقب الإغراق بالرسائل والتلاعب والناس اللي تستعمله كبوت مجاني.',
      },
    ],
    note: 'مبني على قاعدة معرفتك · وسيناريوهات جاهزة للعقارات والطاقة الشمسية والضيافة والبقالة',
  },
  comments: {
    aria: 'أتمتة التعليقات',
    shotAlt:
      'مدير التعليقات بـ DilaCRM يعرض تعليقاً على Instagram بالكوردية السورانية، ومعاه رد كتبه الذكاء الاصطناعي بانتظار الموافقة.',
    title: 'التعليقات تصير زبائن',
    lede: 'تعليقات Facebook وInstagram وTikTok بطابور واحد. الـ AI يكتب الرد العلني والرسالة الخاصة، ويخفي السبام المسيء، ويرد بالسوراني إذا التعليق بالسوراني.',
    rows: [
      {
        title: 'من التعليق للرسالة الخاصة، تلقائياً',
        body: '«اكتب PRICE بالتعليق وراح نراسلك.» قواعد الكلمات المفتاحية تحوّل تعليقات الإعلانات لمحادثات خاصة، على طريقة ManyChat، بدون اشتراك ثاني.',
      },
      {
        title: 'إشراف ما ينام',
        body: 'الإساءة تنصنّف وتنخفي تلقائياً، بحد يومي، وبتفعيل لكل قناة، وسجل تدقيق كامل.',
      },
    ],
  },
  campaigns: {
    aria: 'الحملات والإعلانات',
    title: 'حملات توصل فعلاً',
    body: 'قوالب WhatsApp المعتمدة ترسلها للكل، أو للغة وحدة، أو لمجموعة الـ VIP، أو للزبائن الأوفياء اللي يرجعون. كل مستلم نتابعه من الإرسال لين القراءة، وكل رسالة تفشل ترجع فلوسها لمحفظتك.',
    shotAlt:
      'منشئ حملات WhatsApp بـ DilaCRM يعرض رصيد الاعتماد، واختيار القالب، وخيارات الجمهور، والتكلفة المتوقعة.',
    adsTitle: 'مصروف الإعلانات، أخيراً بشفافية',
    adsBody:
      'حسابات Meta وTikTok جنب بعض: المصروف والوصول وCTR وCPC والتحويلات وROAS. توقف حملة أو تعدل ميزانية بدون ما تطلع من الشاشة.',
    adsAlt:
      'لوحة أداء الإعلانات بـ DilaCRM لحساب TikTok تعرض المصروف والظهور والنقرات والتحويلات وROAS، مع مخطط للمصروف عبر الزمن.',
    metricsAria: 'مؤشرات الإعلانات المتابعة',
    metrics: ['المصروف', 'الوصول', 'CTR', 'CPC', 'CPM', 'التحويلات', 'ROAS'],
  },
  crm: {
    aria: 'مسار المبيعات بـ CRM',
    title: 'نظام CRM يعبّي نفسه بنفسه',
    lede: 'جهات الاتصال تنولد من المحادثات، والصفقات تحمل قيمتين، IQD وUSD، وخط المبيعات يخبرك أي عميل محتمل راح يبرد قبل لا يبرد.',
    shotAlt:
      'لوحة Kanban بـ DilaCRM فيها مراحل استفسار جديد ومؤهل وزيارة موقع وعرض سعر، وقيم الصفقات بـ IQD وUSD.',
    rows: [
      {
        title: 'تنبيهات الخمول لكل مرحلة',
        body: 'كل مرحلة إلها حد خاص «سكت بعد N يوم»، فتنطلق المتابعة قبل لا ينساك العميل المحتمل.',
      },
      {
        title: 'ساخن، دافئ، بارد بنظرة وحدة',
        body: 'درجة حرارة العميل المحتمل وآخر إشارة من الـ AI وأفضل خطوة جاية تكون على البطاقة، والتوزيع الدوّار يوزع العملاء الجدد بعدل.',
      },
      {
        title: 'استيراد بدون معارك الجداول',
        body: 'ارفع ملفات CSV وExcel لين 5,000 صف مع مطابقة الأعمدة بالـ AI، وميزة دمج المكررات اللي تحافظ على كل المحادثات.',
      },
    ],
  },
  stats: {
    aria: 'DilaCRM بالأرقام',
    labels: ['قنوات مراسلة', 'لغات الواجهة', 'لغات ردود الـ AI', 'الـ AI بالخدمة'],
  },
  languages: {
    aria: 'اللغات',
    title: 'الصندوق الوحيد اللي يحچي سوراني',
    lede: 'واجهة كاملة من اليمين لليسار، وذكاء اصطناعي يرد بالكوردية السورانية أو العربية أو الإنكليزية بدون ما تطلب منه مرتين. زبائنك يكتبون بطريقتهم، وهو يلاقيهم بنفس الطريقة.',
    rtlAlt:
      'صندوق رسائل DilaCRM معكوس بالكامل بالعربية، يعرض الواجهة من اليمين لليسار وبطاقة المحفظة وقائمة خطوات الإعداد.',
    rtlCaption: 'اتجاه كامل من اليمين لليسار، عربي وكوردي',
    channelsAlt:
      'صفحة ربط القنوات بـ DilaCRM لـ Meta وWhatsApp وTikTok، مع خانات اختيار لكل خدمة وربط WhatsApp عبر رمز QR.',
    channelsCaption: 'اربط قناة بخمس دقايق',
  },
  demo: {
    title: 'لا تخسر عميل الساعة 11:47 مساءً',
    sub: 'عميل محتمل راسلك الساعة 11:47 مساءً. DilaCRM رد عليه قبل لا يروح. سوّي هالشي على كل قناة عندك.',
    secondary: 'افتح صندوق الرسائل المباشر',
  },
};

const ckb: Copy = {
  seo: {
    title: 'DilaCRM: ئینبۆکسی زیرەکی دەستکرد بۆ WhatsApp‏، Instagram‏، Telegram و زیاتر',
    description:
      'DilaCRM لەلایەن Datacode هەموو کەناڵەکانی کڕیارەکانت لە یەک ئینبۆکسی زیرەکدا کۆ دەکاتەوە: WhatsApp‏، Instagram‏، Messenger‏، Telegram‏، TikTok و ئیمەیل، لەگەڵ وەڵامی زیرەکی دەستکرد بە کوردی، عەرەبی و ئینگلیزی، ئۆتۆماتیککردنی کۆمێنت، کەمپەینی WhatsApp، شیکاری ڕیکلام و سیستەمی CRMی ناوخۆیی.',
  },
  hero: {
    title: 'هەموو پەیامێک مامەڵەیە',
    lede: 'یەک ئینبۆکس بۆ WhatsApp‏، Instagram‏، Messenger‏، Telegram‏، TikTok و ئیمەیل، لەگەڵ زیرەکی دەستکرد کە بە زمانی کڕیارەکەت ڕەشنووسی وەڵام دەنووسێت.',
    secondary: 'سەیری ئینبۆکسەکە بکە',
    liveLabel: 'ئینبۆکسی زیندوو',
    imageAlt:
      'ئینبۆکسی DilaCRM کە وەڵامێکی پێشنیارکراوی زیرەکی دەستکرد نیشان دەدات و چاوەڕێی پەسەندکردنی مرۆڤە، لەگەڵ دوگمەکانی ناردن، دەستکاری و نەناردن.',
    chipDraft: 'ڕەشنووسی AI · چاوەڕێی پەسەندکردن',
    chipEmail: 'ئیمەیل',
  },
  marquee: [
    'WhatsApp',
    'نامەکانی Instagram',
    'Messenger',
    'Telegram',
    'TikTok',
    'ئیمەیل',
    'زیرەکی دەنگی',
    'کوردی',
    'العربية',
    'English',
  ],
  inbox: {
    aria: 'ئینبۆکسی یەکگرتوو',
    title: 'یەک ئینبۆکس. هەموو کەناڵەکان.',
    lede: 'هەژماری WhatsApp کە ئێستا بەکاری دەهێنیت، Instagram، پەیجەکەت، بۆتی Telegram و ئیمەیلەکەت بەستەوە. هەموو شتێک دەچێتە یەک ڕیزەوە کە هێڵی جیاواز، دواخستنی نامە و نیشاندانی ئامادەبوونی تێدایە.',
    rows: [
      {
        title: 'زانیاری لەسەر هەموو گفتوگۆیەک',
        body: 'زانیاری پەیوەندی، تاگ، مامەڵە و پلەی گەرمی کڕیاری ئەگەری لە تەنیشت گفتوگۆکەن، نەک لە تابێکی تر.',
      },
      {
        title: 'کاتەکە دیارە',
        body: 'پەنجەرەی نامەناردنی هەر کەناڵێک بەدوادا دەچین، و کاتێک پەنجەرەکە دادەخرێت، دەتوانیت قاڵبێکی پەسەندکراو بەکاربهێنیت.',
      },
    ],
    shotAlt:
      'بینینی گفتوگۆ لە DilaCRM لەگەڵ گفتوگۆی Messenger، پانێلی CRM لە لا، کارتی کڕیاری ئەگەری و ژمێرەری پەنجەرەی وەڵامدانەوە.',
  },
  loop: {
    aria: 'پەسەندکردنی زیرەکی دەستکرد',
    kicker: 'مرۆڤ لە ناو پرۆسەکەدا',
    title: 'AI ڕەشنووس دەنووسێت. تۆ پەسەندی دەکەیت.',
    lede: 'سێ دۆخی سەربەخۆیی هەیە، بۆ هەر کەناڵێک بە جیا چالاکی دەکەیت. ئەو وەڵامانەی متمانەیان بەرزە دەتوانن خۆیان بچن، هەموو شتێکی تر وەک ڕەشنووس دەوەستێت لەگەڵ هۆکاری وەستانەکەی. هیچ شتێک بێ چاودێری نانێردرێت.',
    rows: [
      {
        title: 'تەنها ڕەشنووس',
        body: 'AI دەنووسێت، مرۆڤ دەینێرێت. هەموو وەڵامێک چاوەڕێی پەسەندکردن، دەستکاری یان ڕەتکردنەوە دەکات لەگەڵ هۆکارێک کە لێی فێر دەبێت.',
      },
      {
        title: 'کاتێک دڵنیایە وەڵام دەداتەوە',
        body: 'ئەگەر لە سنووری متمانەی تۆ زیاتر بێت، دەینێرێت و دواتر بەدواداچوون ئامادە دەکات. ئەگەر کەمتر بێت، ڕەشنووسەکە دەچێتە ڕیزەکەی تۆوە.',
      },
      {
        title: 'بە تەواوی سەربەخۆ',
        body: 'بۆ هەر کەناڵێک چالاکی بکە، لەگەڵ پاراستنێک کە چاودێری ئاڵۆزکردن بە نامە، دەستکاریکردن و ئەو کەسانە دەکات کە وەک چاتبۆتی بێ بەرامبەر بەکاری دەهێنن.',
      },
    ],
    note: 'لەسەر بنەمای بنکەی زانیاری تۆ · سیناریۆی ئامادە بۆ خانووبەرە، وزەی خۆر، میوانداری و بەقاڵی',
  },
  comments: {
    aria: 'ئۆتۆماتیککردنی کۆمێنت',
    shotAlt:
      'بەڕێوەبەری کۆمێنتەکانی DilaCRM کە کۆمێنتێکی Instagram بە کوردی سۆرانی نیشان دەدات، لەگەڵ وەڵامێکی ڕەشنووسکراو بە زیرەکی دەستکرد کە چاوەڕێی پەسەندکردنە.',
    title: 'کۆمێنتەکان دەبنە کڕیار',
    lede: 'کۆمێنتەکانی Facebook‏، Instagram و TikTok لە یەک ڕیزدا. AI وەڵامی گشتی و نامەی تایبەت ڕەشنووس دەکات، سپامی ناشیرین دەشارێتەوە و ئەگەر کۆمێنتەکە بە سۆرانی بێت بە سۆرانی وەڵام دەداتەوە.',
    rows: [
      {
        title: 'لە کۆمێنتەوە بۆ نامەی تایبەت، خۆکارانە',
        body: '«کۆمێنت بکە PRICE و نامەت بۆ دەنێرین.» ڕێساکانی وشەی کلیلی کۆمێنتی ڕیکلام دەکەنە گفتوگۆی نامەی تایبەت، بە شێوازی ManyChat، بێ بەشداریکردنێکی تر.',
      },
      {
        title: 'چاودێرییەک کە هەرگیز نانوێت',
        body: 'بەکارهێنانی خراپ پۆلێن دەکرێت و خۆکارانە دەشاردرێتەوە، بە سنووری ڕۆژانە، چالاککردن بۆ هەر کەناڵێک و تۆماری پشکنینی تەواو.',
      },
    ],
  },
  campaigns: {
    aria: 'کەمپەین و ڕیکلام',
    title: 'کەمپەینێک کە دەگاتە ئامانج',
    body: 'قاڵبە پەسەندکراوەکانی WhatsApp بۆ هەمووان، بۆ یەک زمان، بۆ کڕیارە تایبەتەکانت (VIP)، یان بۆ کڕیارە دڵسۆزەکان. هەر وەرگرێک لە ناردنەوە تا خوێندنەوە بەدوادا دەچین، هەر نامەیەکی شکستخواردووش پارەکەی دەگەڕێتەوە بۆ جزدانەکەت.',
    shotAlt:
      'دروستکەری کەمپەینی WhatsApp لە DilaCRM لەگەڵ باڵانسی کرێدیت، هەڵبژاردنی قاڵب، بژاردەکانی بینەر و تێچووی مەزەندەکراو.',
    adsTitle: 'خەرجی ڕیکلام، دواجار ڕاستگۆیانە',
    adsBody:
      'هەژمارەکانی Meta و TikTok لە تەنیشت یەکترەوە: خەرجی، گەیشتن، CTR‏، CPC، گۆڕین و ROAS. کەمپەینێک ڕابگرە یان بودجەیەک ڕێک بخە بەبێ ئەوەی شاشەکە جێبهێڵیت.',
    adsAlt:
      'داشبۆردی کارایی ڕیکلامەکانی DilaCRM بۆ TikTok کە خەرجی، بینین، کلیک، گۆڕین و ROAS نیشان دەدات لەگەڵ هێڵکاری خەرجی بە درێژایی کات.',
    metricsAria: 'پێوەرەکانی ڕیکلام کە بەدوایاندا دەچین',
    metrics: ['خەرجی', 'گەیشتن', 'CTR', 'CPC', 'CPM', 'گۆڕین', 'ROAS'],
  },
  crm: {
    aria: 'پایپلاینی CRM',
    title: 'سیستەمی CRM کە خۆی پڕ دەبێتەوە',
    lede: 'کەسەکانی پەیوەندی لە گفتوگۆکانەوە دروست دەبن، مامەڵەکان دوو نرخیان هەیە، IQD و USD، و پایپلاینەکە پێت دەڵێت کام کڕیاری ئەگەری لە ساردبوونەوەدایە پێش ئەوەی ساردببێتەوە.',
    shotAlt:
      'پایپلاینی Kanban لە DilaCRM لەگەڵ قۆناغەکانی داواکاری نوێ، پەسەندکراو، سەردانی شوێن و نرخدان، کە نرخی مامەڵەکان بە IQD و USD تێدایە.',
    rows: [
      {
        title: 'ئاگادارکردنەوەی خامۆشی بۆ هەر قۆناغێک',
        body: 'هەر قۆناغێک سنوورێکی تایبەتی خۆی هەیە «دوای N ڕۆژ بێدەنگ بوو»، بۆیە بەدواداچوون پێش ئەوە دەست پێدەکات کە کڕیارە ئەگەرییەکە تۆ لەبیر بکات.',
      },
      {
        title: 'گەرم، نیوەگەرم، سارد بە یەک تەماشا',
        body: 'پلەی گەرمی کڕیاری ئەگەری، دوایین نیشانەی AI و باشترین هەنگاوی داهاتوو لەسەر کارتەکەیە، و دابەشکردنی خولی کڕیارە ئەگەرییە نوێیەکان بە یەکسانی دابەش دەکات.',
      },
      {
        title: 'هاوردەکردن بێ شەڕی خشتە',
        body: 'فایلی CSV و Excel هەتا 5,000 ڕیز باربکە لەگەڵ ڕێکخستنی ستوونەکان بە AI، هەروەها یەکخستنی دووبارەکان کە هەموو گفتوگۆکان دەپارێزێت.',
      },
    ],
  },
  stats: {
    aria: 'DilaCRM بە ژمارە',
    labels: ['کەناڵی نامەناردن', 'زمانی ڕووکار', 'زمانی وەڵامی AI', 'AI لە ئەرکدایە'],
  },
  languages: {
    aria: 'زمانەکان',
    title: 'تاکە ئینبۆکس کە سۆرانی قسە دەکات',
    lede: 'ڕووکارێکی تەواو لە ڕاستەوە بۆ چەپ، و زیرەکییەکی دەستکرد کە بە کوردی سۆرانی، عەرەبی یان ئینگلیزی وەڵام دەداتەوە بێ ئەوەی دووجار داوای لێبکەیت. کڕیارەکانت بە شێوازی خۆیان دەنووسن، ئەویش لەو شێوازەدا پێیان دەگات.',
    rtlAlt:
      'ئینبۆکسی DilaCRM بە تەواوی بە عەرەبی هەڵگەڕاوەتەوە، ڕووکاری ڕاست بۆ چەپ، کارتی جزدان و لیستی ڕێکخستنەکان نیشان دەدات.',
    rtlCaption: 'ڕاست بۆ چەپی تەواو، عەرەبی و کوردی',
    channelsAlt:
      'لاپەڕەی بەستنەوەی کەناڵەکانی DilaCRM بۆ Meta‏، WhatsApp و TikTok لەگەڵ چوارگۆشەی هەڵبژاردن بۆ هەر خزمەتگوزارییەک و بەستنەوەی WhatsApp بە QR.',
    channelsCaption: 'کەناڵێک لە پێنج خولەکدا ببەستەوە',
  },
  demo: {
    title: 'ئیتر کڕیاری 11:47ی شەو لەدەست مەدە',
    sub: 'کڕیارێکی ئەگەری کاتژمێر 11:47ی شەو نامەی نارد. DilaCRM پێش ئەوەی بڕوات وەڵامی دایەوە. ئەمە لەسەر هەموو کەناڵێک بکە کە هەتە.',
    secondary: 'ئینبۆکسی زیندوو بکەرەوە',
  },
};

export const dilaCrmCopy = defineCopy(en, ar, ckb);
